// Converts unknown/structured values into a plain-text string for display/sending.
// Goal: avoid leaking "[object Object]" into chat, while preserving meaningful text.
export function coerceToPlainText(value) {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string') return value;

  const t = typeof value;
  if (t === 'number' || t === 'boolean' || t === 'bigint') return String(value);

  if (value instanceof Date) return value.toISOString();

  if (Array.isArray(value)) {
    return value
      .map((v) => coerceToPlainText(v))
      .map((s) => String(s || '').trim())
      .filter(Boolean)
      .join('\n');
  }

  if (t === 'object') {
    // Common content-bearing keys
    const candidates = [
      value.text,
      value.plainText,
      value.content,
      value.value,
      value.label,
      value.title,
      value.question,
      value.message,
    ];

    for (const cand of candidates) {
      if (typeof cand === 'string' && cand.trim()) return cand;
    }

    // Quill delta-like shape: { ops: [{ insert: "..." }, ...] }
    if (Array.isArray(value.ops)) {
      const joined = value.ops
        .map((op) => (op && typeof op.insert === 'string' ? op.insert : ''))
        .join('');
      if (joined.trim()) return joined;
    }

    // Fallback to JSON when possible (prefer readable vs [object Object])
    try {
      const json = JSON.stringify(value, null, 2);
      return typeof json === 'string' ? json : '';
    } catch {
      // Circular or non-serializable object. Try to extract a few meaningful primitive fields.
      try {
        const keys = Object.keys(value).slice(0, 30);
        const parts = [];
        for (const key of keys) {
          const v = value[key];
          if (typeof v === 'string' && v.trim()) parts.push(v.trim());
          else if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') parts.push(String(v));
        }
        const joined = parts.filter(Boolean).join('\n');
        if (joined.trim()) return joined;
      } catch {
        // ignore
      }
    }
  }

  try {
    const s = String(value);
    // Avoid propagating the useless default object string.
    if (s === '[object Object]') return '';
    return s;
  } catch {
    return '';
  }
}
